import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { LocalizedString } from "app/shared/model/localized-string.model";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { environment } from "~env";
import { ExerciseStatus, InscriptionStatus, MemberRegistration } from "./member-registration.model";
import { AnnualRegistration, MemberRegistrationSettings } from "./year-selection.model";

const API_URL = environment.projectionQueryApi;
const REFERENCE_TABLE_URL = environment.referenceTableQueryApi;

@Injectable({
  providedIn: 'root',
})
export class YearSelectionOdataQueryService {

  constructor(private http: HttpClient) { }

  loadMemberRegistrations(memberId: string): Observable<MemberRegistration[]> {
    return this.http.get(`${API_URL}/memberRegistrations?$filter=MemberId eq ${memberId}&$orderby=ValidFrom desc, CreatedOn desc, LastUpdatedOn desc`)
      .pipe(
        map(res => {
          const result = res['value'].map(item => this.parseRegistration(item));
          return !!result ? this.uniqByYear(result) : null;
        }),
      );
  }

  getMemberRegistrationSettings(memberId: string): Observable<MemberRegistrationSettings> {
    return this.http.get(`${API_URL}/memberRegistrationSettings?$filter=MemberId eq ${memberId}`)
      .pipe(
        map(res => this.parseRegistrationSettings(res['value'][0]))
      );
  }

  getMemberFirstDatePractice(memberId: string): Observable<Date> {
    return this.http.get(`${API_URL}/memberPracticeTypes?$filter=MemberId eq ${memberId}&$orderby=EffectiveOn asc&$top=1`)
      .pipe(
        map(res => new Date(res['value'][0].EffectiveOn)));
  }


  getAnnualRegistrations(): Observable<AnnualRegistration[]> {
    var temp = [];
    return this.http
      .get(`${REFERENCE_TABLE_URL}/AnnualRegistration?$orderby=Year desc`)
      .pipe(
        map(res => {
          temp = res['value'].map((item) => this.parseAnnualRegistration(item));
          return temp;
        })
      )
  }

  private parseRegistration(item: any): MemberRegistration {

    const res = {
      id: item.Id,
      memberId: item.MemberId,
      period: item.Period,
      inscriptionStatus: null,
      exerciseStatus: null,
      cancelled: item.Cancelled,
      memberPracticeTypeId: item.MemberPracticeTypeId,
      memberPracticeType: new LocalizedString(item.MemberPracticeType_Fr, item.MemberPracticeType_En),
      validFrom: new Date(item.ValidFrom),
      validTo: new Date(item.ValidTo),
      createdOn: new Date(item.CreatedOn),
    }

    switch (item.BaseInscriptionStatus) {
      case 'Registered': res.inscriptionStatus = InscriptionStatus.Registered; break;
      case 'NotRegistered': res.inscriptionStatus = InscriptionStatus.NotRegistered; break;
      case 'PermanentNotRegistered': res.inscriptionStatus = InscriptionStatus.PermanentNotRegistered; break;
      default: res.inscriptionStatus = InscriptionStatus.NotAvailable; break;
    }

    switch (item.BaseExerciseStatus) {
      case 'Active': res.exerciseStatus = ExerciseStatus.Active; break;
      case 'Inactive': res.exerciseStatus = ExerciseStatus.Inactive; break;
      default: res.exerciseStatus = ExerciseStatus.NotAvailable; break;
    }

    return res
  }

  private parseRegistrationSettings(item: any): MemberRegistrationSettings {
    return {
      id: item?.Id,
      memberId: item?.MemberId,
      allowChequePayment: item?.AllowCheckPayment,
      disableAccessToForm: item?.DisableAccessToForm,
      identificationCode: item?.IdentificationCode
    }
  }

  private uniqByYear(array: MemberRegistration[]): MemberRegistration[] {

    const arrayOfPeriods = array.reduce((acc, curr) => {
      if (acc.indexOf(curr.period) === -1) {
        acc.push(curr.period);
      }
      return acc
    }, []);

    return arrayOfPeriods.map(period => array.find(reg => reg.period === period));
  }

  private parseAnnualRegistration(item: any): AnnualRegistration {
    return {
      id: item.Id,
      year: item.Year,
      startDate: new Date(item.StartDate),
      endDate: new Date(item.EndDate),
      isActive: item.IsActive,
      description: { fr: item?.Description?.Fr, en: item?.Description?.En },
      formOpeningDate: new Date(item.FormOpeningDate)
    };
  }

}